import { useContext } from 'react';

import { useQueryClient, useMutation } from '@tanstack/react-query';
import axios from 'axios';

import { ConfigContext } from '../../../aurr/contexts/ConfigContext';
import LoginContext from '../../../aurr/contexts/LoginContext';
import type { Collection } from '../../../common/type';
import {
  COLLECTION_ITEM_DELETE_KEY,
  COLLECTION_ITEM_QUERY_KEY,
  COLLECTION_LIST_QUERY_KEY,
  COLLECTION_QUERY_KEY,
} from '../constants';

export const useDeleteCollectionItems = (params: {
  id: string;
  baseURL?: string;
  provider?: string;
}) => {
  const { COMMUNITY_FUNCTIONS_URL } = useContext(ConfigContext);
  const { baseURL, id, ...restParams } = params;
  const { access_token, userProfile } = useContext(LoginContext);

  const parsedBaseURL = baseURL || COMMUNITY_FUNCTIONS_URL;

  const axiosConfig = {
    baseURL: `${parsedBaseURL}/store/v1/collections/${id}/items`,
    headers: {
      ...(access_token && { authorization: `Bearer ${access_token}` }),
    },
  };
  const api = axios.create(axiosConfig);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [COLLECTION_ITEM_DELETE_KEY, parsedBaseURL, id],
    mutationFn: async (itemKeys: string[]) => {
      return Promise.all(
        itemKeys.map(itemKey =>
          api.delete(`/${itemKey}`, restParams ? { params: restParams } : {})
        )
      );
    },
    onMutate: async itemKeys => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: [COLLECTION_QUERY_KEY, parsedBaseURL, id],
      });

      await queryClient.cancelQueries({
        queryKey: [COLLECTION_LIST_QUERY_KEY, parsedBaseURL, userProfile?.sub],
      });

      await queryClient.cancelQueries({
        queryKey: [COLLECTION_ITEM_QUERY_KEY, parsedBaseURL, id],
      });

      // Snapshot the previous value
      const savedCollections = queryClient.getQueryData([
        COLLECTION_QUERY_KEY,
        parsedBaseURL,
        id,
      ]);

      queryClient.setQueryData<Collection | undefined>(
        [COLLECTION_QUERY_KEY, parsedBaseURL, id],
        old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            collection_items: old.collection_items.filter(
              ({ item_key }) => !itemKeys.includes(item_key)
            ),
          };
        }
      );

      queryClient.setQueryData<Collection[] | undefined>(
        [COLLECTION_LIST_QUERY_KEY, parsedBaseURL, userProfile?.sub],
        old => {
          if (!old || !old?.length) {
            return old;
          }
          return old.map(collection => ({
            ...collection,
            isOptimistic: true,
            collection_items: old[0].collection_items.filter(
              ({ item_key }) => !itemKeys.includes(item_key)
            ),
          }));
        }
      );

      // Return a context object with the snapshotted value
      return { savedCollections };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (err, newTodo, context) => {
      queryClient.setQueryData(
        [COLLECTION_QUERY_KEY, parsedBaseURL, id],
        context?.savedCollections
      );
    },
    // Always refetch after error or success:
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_LIST_QUERY_KEY, parsedBaseURL, userProfile?.sub],
      });
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEY, parsedBaseURL, id],
      });
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_ITEM_QUERY_KEY, parsedBaseURL, id],
      });
    },
  });
};
